import { usersControllerGetProfile } from "@mebi/api-client";
import { organizerService } from "@/features/organizers/services/organizer.service";
import {
  checkProfileIncompleteness,
  extractRoles,
  extractUserData,
  extractUserProfile,
  normalizeProfileData,
} from "./profileUtils";

const PROFILE_CHECK_TIMEOUT_MS = 4000;

const resolveErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message.toLowerCase();
  }

  if (typeof error === "string") {
    return error.toLowerCase();
  }

  if (typeof error === "object" && error !== null) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") {
      return message.toLowerCase();
    }
  }

  return "";
};

export const isRecoverableProfileCheckError = (error: unknown): boolean => {
  if (globalThis.navigator?.onLine === false) {
    return true;
  }

  const message = resolveErrorMessage(error);

  return (
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("network request failed") ||
    message.includes("load failed") ||
    message.includes("offline") ||
    message.includes("timeout") ||
    message.includes("timed out") ||
    message.includes("abort")
  );
};

export const resolveWithTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
): Promise<T | null> => {
  return new Promise<T | null>((resolve, reject) => {
    const timeoutId = setTimeout(() => resolve(null), timeoutMs);

    promise
      .then((value) => {
        clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });
};

export const checkProfileCompletion = async (
  roleHint?: string,
): Promise<boolean> => {
  try {
    console.log("[NavigationUtils] Checking profile completion via API...", {
      roleHint,
    });
    const response = await resolveWithTimeout(
      usersControllerGetProfile(),
      PROFILE_CHECK_TIMEOUT_MS,
    );
    if (!response) {
      return false;
    }

    const data = response;

    if (!data) {
      console.warn("[NavigationUtils] Profile data is empty/null");
      return true;
    }

    const profileData = normalizeProfileData(data);
    if (!profileData) {
      console.warn("[NavigationUtils] Profile data normalization failed");
      return true;
    }

    console.log("[NavigationUtils] Extracted profile data:", profileData);

    const userData = extractUserData(profileData);
    const userProfile = extractUserProfile(profileData);

    console.log(
      "[NavigationUtils] DEBUG - Profile Data Structure:",
      JSON.stringify(profileData, null, 2),
    );
    console.log(
      "[NavigationUtils] DEBUG - User Data Structure:",
      JSON.stringify(userData, null, 2),
    );

    const roles = extractRoles(profileData, userData);

    if (
      roleHint &&
      !roles.some((r) => r.toLowerCase() === roleHint.toLowerCase())
    ) {
      console.log(
        `[NavigationUtils] Adding roleHint '${roleHint}' to detected roles`,
      );
      roles.push(roleHint);
    }

    const isOrganizer = roles.some((r) => r.toLowerCase() === "organizer");

    console.log("[NavigationUtils] DEBUG - Role Detection:", {
      roles,
      isOrganizer,
    });

    return checkProfileIncompleteness(userData, userProfile, roles);
  } catch (error) {
    console.error("[NavigationUtils] Profile check failed:", error);
    return !isRecoverableProfileCheckError(error);
  }
};

export const checkOrganizerVerification = async (): Promise<boolean> => {
  try {
    const organizationStatus = await organizerService.getVerificationStatus();
    if (organizationStatus?.status !== "verified") {
      console.log(
        "[NavigationUtils] Organizer not verified, redirecting to Role Selection",
      );
      return false;
    }
    return true;
  } catch (error) {
    console.error(
      "[NavigationUtils] Error checking organization status:",
      error,
    );
    return false;
  }
};

export const getTargetRouteForRole = (role: string): string => {
  switch (role.toLowerCase()) {
    case "player":
      return "/tabs/home";
    case "organizer":
      return "/organizer-tabs/newsfeed";
    default:
      return "/tabs/home";
  }
};
